import React, { useEffect, useState } from 'react';
import { usePageConfig } from '../hooks/usePageConfig';
import Navbar from './Navbar';
import {
  Hero,
  LogosMarquee,
  BoringSection,
  PortfolioSection,
  ReviewsSection,
  HowItWorksSection,
  Footer,
  TestimonialsSection,
  LocationSection,
  CategoriesSection
} from './Sections';
import EditorPanel from './EditorPanel';
import '../styles/jerseybird.css';
import { getClient2, supbaseManager } from '../API';


/**
 * JerseyBirdPage
 *
 * Drop this anywhere in your React app:
 *   import JerseyBirdPage from './components/JerseyBirdPage';
 *   <JerseyBirdPage />
 *
 * Props:
 *   initialConfig  – (optional) override the default config on mount
 *   showEditor     – (optional, default true) show the CMS editor button
 *   onConfigChange – (optional) callback fired on every config change, receives the full config object
 *
 * Catalog mode (URL query string):
 *   ?s=<clientId>  – loads the client's shared landing config automatically.
 *                    Sets window.is_catalog = true and hides the editor.
 */
export default function JerseyBirdPage({
  initialConfig,
  showEditor = true,
  onConfigChange,
}) {
  const { config, updateConfig, resetConfig, exportConfig, importConfig } =
    usePageConfig();

  // Track whether we're in catalog/shared mode and whether loading is in progress
  const [isCatalog, setIsCatalog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // ─── Catalog mode: read ?s=<clientId> from the URL ───────────────────────
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const clientId = urlParams.get('site');

    if (!clientId) {
      // Normal mode: apply optional override and stop here
      if (initialConfig) importConfig(initialConfig);
      return;
    }

    // Mark as catalog so the editor is hidden
    window.is_catalog = true;
    setIsCatalog(true);
    setIsLoading(true);

    (async () => {
      try {
        // getClient must be available in scope – import it at the top of this
        // file (or pass it as a prop) according to your project structure.
        // e.g.: import { getClient } from '../services/clientService';
        const client = await getClient2(clientId);

        if (!client?.[0]) {
          throw new Error(`Cliente não encontrado para s=${clientId}`);
        }

        const ownerId = client[0].client_id;

        // Exposto globalmente pro DesignModal (renderizado bem lá dentro das
        // seções) conseguir achar o dono da loja e buscar o WhatsApp certo
        // pra abrir com o pedido "Tenho interesse".
        window.jbOwnerId = ownerId;

        // 1. Carrega config da landing do Supabase
        if (window.loadLandingConfig) {
          window.loadLandingConfig(ownerId);
        }

        // 2. Carrega categorias do dono (mesmo fluxo do formPopulate)
        const categoriesRaw = await supbaseManager('get', 'categories_new', null, ownerId);
        const categoriesSharedAll = await supbaseManager('all', 'categories_new');

        // filtra compartilhadas com qualquer usuário (shared = true no json)
        const categoriesShared = (categoriesSharedAll || []).filter(c => {
          try {
            return JSON.parse(c.json)?.shared === true && c.user_id !== ownerId;
          } catch { return false; }
        });

        for (const c of categoriesShared) c.shared = true;

        const allCategories = [...(categoriesRaw || []), ...categoriesShared];

        const parsed = allCategories.map(c => {
          try {
            const p = JSON.parse(c.json);
            return { ...p, dbId: c.category_id, shared: !!c.shared };
          } catch { return null; }
        }).filter(Boolean);

        window.categoriesLoaded = parsed;

        document.body.style.visibility = 'visible';
        try { document.getElementById('loading').style.visibility = 'hidden'; } catch {}
      } catch (err) {
        console.error('Erro ao carregar landing do cliente:', err);
        setLoadError(err.message ?? 'Erro desconhecido');
      } finally {
        setIsLoading(false);
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Notify parent on every config change ────────────────────────────────
  useEffect(() => {
    if (onConfigChange) onConfigChange(config);
  }, [config, onConfigChange]);

  // ─── Sync --jb-red CSS variable ──────────────────────────────────────────
  useEffect(() => {
    document.documentElement.style.setProperty('--jb-red', config.primaryColor);
    const landing = document.getElementById('jb-landing');
    if (landing) landing.style.setProperty('--jb-red', config.primaryColor);
  }, [config.primaryColor]);

  // ─── Loading / error states ───────────────────────────────────────────────
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <p>Carregando...</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <p>Erro ao carregar a página: {loadError}</p>
      </div>
    );
  }

  // Hide the editor in catalog mode
  const editorVisible = showEditor && !isCatalog;

  return (
    <>
      {editorVisible && (
        <EditorPanel
          config={config}
          updateConfig={updateConfig}
          exportConfig={exportConfig}
          importConfig={importConfig}
          onReset={resetConfig}
        />
      )}

      <div id="jb-landing">
        <Navbar config={config} />
        <Hero config={config} />
        <LogosMarquee config={config} />
        <BoringSection config={config} />
        <PortfolioSection config={config} />
        <CategoriesSection config={config} />
        <ReviewsSection config={config} />
        <HowItWorksSection config={config} />
        <TestimonialsSection config={config} />
        <LocationSection config={config} />
        <Footer config={config} />
      </div>
    </>
  );
}